import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  subscribeWorkspaces,
  updateClientDriveInfo,
  updateWorkspace,
  clearClientOrphanDriveFields,
  createNewWorkspaceRef,
  createWorkspace,
  deleteWorkspace,
  deleteWorkspaces,
  linkWorkspaceToProcess,
  serverTimestamp,
} from './AutosDashboardRepository';

import { CrmClientPickerModal } from '../../components/CrmClientPickerModal';
import { SaveCancelBar } from '../../components/ui/SaveCancelBar';
import { CHECKLISTS, PIECE_TYPES, PINNED_PIECE_TYPES } from '../../constants/autosChecklists';
import { resolveCadernoDocumentosV3 } from '../../config/LegalTaxonomy';
import { FIREBASE_CONFIG, TITULAR_SEED } from '../../config/Config';
import { auth, db } from '../../shared/api/firebase/config';
import { formatNPU, isValidNPU, toTitleCasePt } from '../../utils/formatters';
import { createOfficeNotification } from '../../services/NotificationService';

import FileHubArea from './FileHubArea';
import CadernoCockpit from './CadernoCockpit';
import { ProcessosCadernoListView, STATUS_COLUMNS } from './ProcessosCadernoListView';
import { ProcessDossierView } from '../../components/portal/ProcessDossierView';
import { checkMasterIntegrity } from '../../utils/workspaceIntegrityGuard';

const appId = FIREBASE_CONFIG.projectId;

const JURISDICTION_OPTIONS = [
  { value: 'CIVIL', label: 'Direito Civil' },
  { value: 'PENAL', label: 'Direito Penal' },
  { value: 'TRABALHO', label: 'Direito do Trabalho' },
  { value: 'PREVIDENCIARIO', label: 'Direito Previdenciário' },
  { value: 'TRIBUTARIO', label: 'Direito Tributário' },
  { value: 'ADMINISTRATIVO', label: 'Direito Administrativo' },
  { value: 'FAMILIA', label: 'Direito de Família' },
  { value: 'EMPRESARIAL', label: 'Direito Empresarial' },
  { value: 'CONSUMIDOR', label: 'Direito do Consumidor' },
  { value: 'EDUCACIONAL', label: 'Direito Educacional' },
  { value: 'AMBIENTAL', label: 'Direito Ambiental' },
  { value: 'ANIMAL', label: 'Direito Animal' },
  { value: 'DIGITAL', label: 'Direito Digital' },
  { value: 'ELEITORAL', label: 'Direito Eleitoral' },
  { value: 'IMOBILIARIO', label: 'Direito Imobiliário' },
  { value: 'TRIBUNAIS_SUP', label: 'Tribunais Superiores (STJ / STF)' },
];

const LS_WORD_ACTIVE_AUTOS_WORKSPACE = 'rda_word_active_autos_workspace';
const DRIVE_TREE_VERSION = '2026.04-caderno-v3';
const DRIVE_TREE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const getBackendBaseUrl = () =>
 {
  const envUrl = String(import.meta?.env?.VITE_BACKEND_URL || '').trim();
  if (envUrl) return envUrl.replace(/\/+$/, '');
  return 'https://rda-backend-thuq.onrender.com';
};

const readText = (value) => String(value || '').trim();

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') {
    try {
      return value.toDate().getTime();
    } catch {
      return 0;
    }
  }
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

const shouldRunDriveAutoHealFromClientSnapshot = (client) => {
  const driveFolderId = readText(client?.driveFolderId || client?.drive?.rootFolderId || client?.drive?.folderId);
  if (!driveFolderId) return false;

  const driveFolders = client?.driveFolders;
  const healthy = client?.driveTreeHealthy !== false;
  const version = readText(client?.driveTreeVersion);
  const lastCheckedAt = toMillis(client?.driveTreeLastCheckedAt);
  const isFresh = lastCheckedAt > 0 && (Date.now() - lastCheckedAt) < DRIVE_TREE_MAX_AGE_MS;
  const hasFolders = driveFolders && typeof driveFolders === 'object' && readText(driveFolders?.processos);

  if (!healthy) return true;
  if (!hasFolders) return true;
  if (version !== DRIVE_TREE_VERSION) return true;
  if (!isFresh) return true;
  return false;
};


const toDateLabel = (value) => {
  try {
    if (!value) return '';
    if (typeof value.toDate === 'function') return value.toDate().toLocaleString('pt-BR');
    if (typeof value === 'number') return new Date(value).toLocaleString('pt-BR');
    return '';
  } catch {
    return '';
  }
};

export default function AutosDashboard({
  newCadernoTrigger = 0,
  extSearch = null, onExtSearchChange = null,
  extFilterType = null, onExtFilterTypeChange = null,
}) {
  // ─── ARQUITETURA MPA ───────────────────────────────────────────────────
  // A guilhotina window.Word foi PURGADA: com Multiple Entry Points, o Word
  // carrega /taskpane.html → src/taskpane.jsx → FileHubArea direto. Este
  // AutosDashboard é EXCLUSIVO da SPA web e nunca é instanciado no Word.

  const [activeWorkspace, setActiveWorkspace] = useState(null);
  const [activeDossierPid, setActiveDossierPid] = useState(null);
  const suppressWordAutoRestoreRef = useRef(false);
  const creatingWorkspaceRef = useRef(false);
  // MC (15/09/2026, pedido do titular — botão Voltar do navegador e F5):
  // abrir/fechar um caderno só mudava `activeWorkspace` (estado React puro),
  // nunca a URL — Voltar não tinha o que desfazer (não existia entrada de
  // histórico pra essa troca) e F5 sempre recarregava a lista, perdendo o
  // caderno aberto. `?workspaceId=` já era LIDO no mount (useEffect abaixo,
  // achado ao vivo), mas nunca ESCRITO ao abrir — agora `openWorkspace`/
  // fechar caderno também escrevem no `?workspaceId=` via `useSearchParams`
  // (mesmo mecanismo do router, não `window.history` cru — mantém o router
  // e a URL sincronizados). Abrir empurra uma entrada nova (Voltar fecha o
  // caderno e volta pra lista); fechar também empurra, pra ida/volta ficar
  // simétrica.
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInternal, setSearchInternal] = useState('');
  const search = extSearch !== null ? extSearch : searchInternal;
  const setSearch = (v) => { setSearchInternal(v); if (onExtSearchChange) onExtSearchChange(v); };
  const [workspaces, setWorkspaces] = useState([]);
  const [openingWorkspaceId, setOpeningWorkspaceId] = useState('');

  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [isNewOpen, setIsNewOpen] = useState(false);
  const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false);
  const [deletingWorkspaceId, setDeletingWorkspaceId] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isClientPickerOpen, setIsClientPickerOpen] = useState(false);
  const [newClient, setNewClient] = useState(null);
  const [newPieceType, setNewPieceType] = useState('Petição Inicial (Cível)');
  const [newProcessMode, setNewProcessMode] = useState('existing');
  const [newPrimaryProcessId, setNewPrimaryProcessId] = useState('');
  const [newJurisdiction, setNewJurisdiction] = useState([]);
  const [newJurisdictionOpen, setNewJurisdictionOpen] = useState(false);
  const [newJuizo, setNewJuizo] = useState('');
  const [newComarca, setNewComarca] = useState('');
  // MC48 (22/08/2026): completa o MC09 (Fase B), que nunca teve UI própria —
  // até aqui todo caderno novo nascia com status 'Ativo' fixo no payload.
  const [newStatus, setNewStatus] = useState('Ativo');
  const newJurisdictionRef = useRef(null);
  const [linkingWorkspaceId, setLinkingWorkspaceId] = useState('');
  const [linkingNpu, setLinkingNpu] = useState('');
  const [nukeProgress, setNukeProgress] = useState('');
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [legacyInput, setLegacyInput] = useState('');
  const [legacyBusy, setLegacyBusy] = useState(false);
  const [legacyResult, setLegacyResult] = useState(null);

  const workspaceFingerprintRef = useRef('');
  useEffect(() => {
    const unsub = subscribeWorkspaces(
      (snap) => {
        const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        // FIX "CADERNO PULA PRA PRIMEIRA POSIÇÃO AO CLICAR" (22/08/2026,
        // achado pelo titular testando ao vivo): esta era a causa raiz real
        // — sort por updatedAt aqui é a ordem-BASE de `workspaces`, anterior
        // e independente do toggle "Ordenar" de CadernoCockpit.jsx (que já
        // tinha sido corrigido antes, sem efeito, porque o problema nunca
        // esteve lá). Qualquer escrita que tocasse updatedAt de um caderno
        // (ex.: abrir/selecionar) reordenava a lista inteira imediatamente.
        // 2ª correção (22/08/2026, rodada [PET_A]): trocar para createdAt
        // já eliminava o "pular ao clicar", mas o titular pediu uma chave
        // ainda mais robusta — número/nome, não timestamp invisível. Ordem
        // agora é por numeroCNJ (quando existe) ou nome do cliente + alias,
        // sempre alfabética/determinística, com id como desempate final.
        // Nenhum desses campos muda com uso normal (abrir/clicar/autosave).
        const sortKey = (w) => {
          const npu = String(w?.numeroCNJ || w?.ancestry?.primary_process_id || '').trim();
          const client = String(w?.clientName || '').trim().toUpperCase();
          const label = String(w?.alias || w?.pieceType || '').trim().toUpperCase();
          return `${npu ? '0' : '1'}|${npu}|${client}|${label}`;
        };
        const sorted = [...items].sort((a, b) => {
          const diff = sortKey(a).localeCompare(sortKey(b));
          if (diff !== 0) return diff;
          return String(a?.id || '').localeCompare(String(b?.id || ''));
        });
        // Anti-flicker: só atualiza React se dados estruturais mudaram
        const fp = sorted.map((w) => `${w.id}|${w.alias || w.name || ''}|${toMillis(w.updatedAt)}|${toMillis(w.createdAt)}|${w.attachmentsFolderId || ''}`).join(';');
        if (fp === workspaceFingerprintRef.current) return;
        workspaceFingerprintRef.current = fp;
        setWorkspaces(sorted);
      },
      (e) => {
        setError(String(e?.message || e));
      }
    );
    return () => unsub();
  }, []);

  // FIX RESYNC ACTIVE WORKSPACE (20/08/2026): `activeWorkspace` era um snapshot
  // congelado no momento de `openWorkspace` e nunca era atualizado depois — a
  // lista da esquerda (alimentada por `workspaces`, ao vivo via onSnapshot)
  // refletia renomeações/edições, mas o painel direito (FileHubArea, título,
  // "Documentos da Peça"), que lê de `activeWorkspace`, ficava com dado velho
  // até o usuário reabrir o caderno. Aqui, sempre que a entrada correspondente
  // em `workspaces` mudar, `activeWorkspace` é atualizado com os dados frescos.
  const activeWorkspaceSyncFpRef = useRef('');
  useEffect(() => {
    const activeId = String(activeWorkspace?.id || '').trim();
    if (!activeId) return;
    const fresh = workspaces.find((w) => String(w?.id || '') === activeId);
    if (!fresh) return;
    let fp;
    try {
      fp = JSON.stringify(fresh);
    } catch {
      fp = String(fresh?.updatedAt || '');
    }
    if (fp === activeWorkspaceSyncFpRef.current) return;
    activeWorkspaceSyncFpRef.current = fp;
    setActiveWorkspace((prev) => (prev ? { ...prev, ...fresh } : fresh));
  }, [workspaces, activeWorkspace?.id]);

  useEffect(() => {
    if (!newCadernoTrigger) return;
    setIsNewOpen(true);
  }, [newCadernoTrigger]);

  const filtered = useMemo(() => {
    let result = workspaces;

    const filterType = String(extFilterType || '').trim().toLowerCase();
    if (filterType && filterType !== 'todos') {
      result = result.filter((w) => String(w?.pieceType || '').toLowerCase().includes(filterType));
    }

    const t = String(search || '').trim().toLowerCase();
    if (!t) return result;

    return result.filter((w) => {
      const a = String(w?.clientName || '').toLowerCase();
      const b = String(w?.pieceType || '').toLowerCase();
      const c = String(w?.driveFolderId || '').toLowerCase();
      const partiesText = (Array.isArray(w?.partiesRefs) ? w.partiesRefs : [])
        .map((p) => `${p?.role || ''} ${p?.name || ''}`.toLowerCase())
        .join(' ');
      return a.includes(t) || b.includes(t) || c.includes(t) || partiesText.includes(t);
    });
  }, [workspaces, search, extFilterType]);

  const groupedFiltered = useMemo(() => {
    const groups = new Map();
    for (const w of filtered) {
      const key = String(w?.clientName || w?.clientId || '').trim().toUpperCase() || String(w?.id || '');
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(w);
    }
    return Array.from(groups.values());
  }, [filtered]);

  const ensureWorkspaceDriveReady = useCallback(async (workspaceLike) => {
    const workspace = workspaceLike && typeof workspaceLike === 'object' ? workspaceLike : {};
    const workspaceId = readText(workspace?.id);
    const clientId = readText(workspace?.clientId);
    const clientName = readText(workspace?.clientName) || 'Cliente';
    const pieceType = readText(workspace?.pieceType) || 'Processo';
    const baseUrl = getBackendBaseUrl();

    let resolvedDriveFolders = workspace?.driveFolders && typeof workspace.driveFolders === 'object' ? workspace.driveFolders : null;
    let resolvedProcessRootFolderId = readText(workspace?.processRootFolderId || resolvedDriveFolders?.processos || workspace?.driveFolderId);
    let resolvedAttachmentsFolderId = readText(workspace?.attachmentsFolderId || workspace?.driveFolderId);
    let resolvedPieceFolderId = readText(workspace?.processPieceFolderId);

    // [P72] Se o workspace já possui as pastas Drive persistidas, abrir direto sem chamar API do Drive.
    if (resolvedPieceFolderId && resolvedAttachmentsFolderId) {
      return {
        ...workspace,
        driveFolders: resolvedDriveFolders || workspace?.driveFolders || null,
        processRootFolderId: resolvedProcessRootFolderId || null,
        processPieceFolderId: resolvedPieceFolderId,
        attachmentsFolderId: resolvedAttachmentsFolderId,
        driveFolderInput: resolvedAttachmentsFolderId,
        driveFolderId: resolvedAttachmentsFolderId,
      };
    }

    const rootDriveFolderId = readText(
      resolvedDriveFolders?.root ||
      workspace?.rootDriveFolderId ||
      workspace?.clientRootDriveFolderId ||
      workspace?.clientDriveFolderId ||
      workspace?.driveRootFolderId
    );

    if (rootDriveFolderId) {
      if (!baseUrl) {
        throw new Error('Backend não configurado para reconciliar a árvore do Drive do cliente.');
      }

      const reconcileResp = await fetch(`${baseUrl}/drive/reconcile-client-tree`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rootFolderId: rootDriveFolderId,
          clientId,
          clientName,
        }),
      });
      const reconcilePayload = await reconcileResp.json().catch(() => ({}));
      if (!reconcileResp.ok) {
        throw new Error(String(reconcilePayload?.detail || reconcilePayload?.error || 'Falha ao reconciliar a árvore do Drive do cliente.'));
      }

      resolvedDriveFolders = reconcilePayload?.driveFolders && typeof reconcilePayload.driveFolders === 'object'
        ? reconcilePayload.driveFolders
        : resolvedDriveFolders;
      resolvedProcessRootFolderId = readText(resolvedDriveFolders?.processos || resolvedProcessRootFolderId);

      if (clientId) {
        await updateClientDriveInfo(clientId, {
          driveFolderId: rootDriveFolderId,
          driveFolders: resolvedDriveFolders || null,
          drive: resolvedDriveFolders ? {
            rootFolderId: readText(resolvedDriveFolders?.root || rootDriveFolderId),
            triagemFolderId: readText(resolvedDriveFolders?.triagem),
            contratosFolderId: readText(resolvedDriveFolders?.contratos),
            whatsappFolderId: readText(resolvedDriveFolders?.whatsapp),
            processosFolderId: readText(resolvedDriveFolders?.processos),
          } : null,
          driveTreeVersion: DRIVE_TREE_VERSION,
          driveTreeHealthy: true,
          driveTreeLastCheckedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    }

    if (!resolvedProcessRootFolderId) {
      throw new Error('A pasta estrutural Caderno de Autos não está disponível para este caderno.');
    }

    if (!baseUrl) {
      throw new Error('Backend não configurado para garantir a subárvore do caderno de autos.');
    }

    {
      const ensureResp = await fetch(`${baseUrl}/drive/ensure-workspace-folders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          processRootFolderId: resolvedProcessRootFolderId,
          workspaceId: workspaceId,
          pieceType,
          clientId,
          clientName,
        }),
      });
      const ensurePayload = await ensureResp.json().catch(() => ({}));
      if (!ensureResp.ok) {
        throw new Error(String(ensurePayload?.detail || ensurePayload?.error || 'Falha ao garantir a subárvore do caderno de autos.'));
      }

      resolvedPieceFolderId = readText(ensurePayload?.pieceFolder?.id);
      resolvedAttachmentsFolderId = readText(ensurePayload?.attachmentsFolder?.id);
      if (!resolvedPieceFolderId || !resolvedAttachmentsFolderId) {
        throw new Error('Backend não retornou a pasta da peça e a subpasta Anexos para o caderno.');
      }

      const nextWorkspace = {
        ...workspace,
        driveFolders: resolvedDriveFolders || null,
        processRootFolderId: resolvedProcessRootFolderId || null,
        processPieceFolderId: resolvedPieceFolderId || null,
        processPieceFolderName: readText(ensurePayload?.pieceFolder?.name) || null,
        attachmentsFolderId: resolvedAttachmentsFolderId || null,
        attachmentsFolderName: readText(ensurePayload?.attachmentsFolder?.name) || 'Anexos',
        driveFolderInput: resolvedAttachmentsFolderId || null,
        driveFolderId: resolvedAttachmentsFolderId || null,
        lastOpenedAt: serverTimestamp(),
      };

      if (workspaceId) {
        await updateWorkspace(workspaceId, nextWorkspace);
      }

      return nextWorkspace;
    }

    return {
      ...workspace,
      driveFolders: resolvedDriveFolders || workspace?.driveFolders || null,
      processRootFolderId: resolvedProcessRootFolderId || null,
      processPieceFolderId: resolvedPieceFolderId || null,
      attachmentsFolderId: resolvedAttachmentsFolderId || null,
      driveFolderInput: resolvedAttachmentsFolderId || null,
      driveFolderId: resolvedAttachmentsFolderId || null,
    };
  }, []);

  // MC (15/09/2026): separado de `openWorkspace` em duas camadas —
  // `applyWorkspace` só resolve o Drive e seta o estado (sem tocar na URL);
  // `openWorkspace` (usado por clique do usuário) faz isso E empurra a URL.
  // Necessário porque o efeito de sincronização abaixo (que reage ao
  // usuário apertando Voltar/Avançar, ou seja, a URL já mudou sozinha via
  // popstate) precisa aplicar o workspace SEM escrever na URL de novo —
  // senão cada Voltar empurraria uma entrada nova, e o titular precisaria
  // apertar Voltar 2x pra cada 1x que ele queria (o mesmo bug original,
  // só que reintroduzido pela própria correção).
  const applyWorkspace = useCallback(async (workspaceLike) => {
    const workspaceId = readText(workspaceLike?.id);
    setError(null);
    setNotice(null);
    setOpeningWorkspaceId(workspaceId);

    try {
      const readyWorkspace = await ensureWorkspaceDriveReady(workspaceLike);
      suppressWordAutoRestoreRef.current = false;
      setActiveWorkspace(readyWorkspace);
      return true;
    } catch (e) {
      setError(String(e?.message || e));
      return false;
    } finally {
      setOpeningWorkspaceId('');
    }
  }, [ensureWorkspaceDriveReady]);

  const openWorkspace = useCallback(async (workspaceLike) => {
    const workspaceId = readText(workspaceLike?.id);
    const ok = await applyWorkspace(workspaceLike);
    if (!ok) return;
    // Escreve na URL (empurra uma entrada de histórico nova) — é isso que
    // dá ao botão Voltar o que desfazer, e ao F5 o caderno certo pra
    // restaurar (o efeito de sincronização abaixo lê esse mesmo parâmetro).
    // `tab` é removido de propósito: pertence à ABA do caderno que está
    // sendo deixado, não a este — sem isso, abrir um caderno novo pela
    // lista podia "herdar" a aba (ex.: Vínculos) de um caderno anterior,
    // atropelando a regra deliberada de sempre abrir em "Resumo"
    // (CadernoCockpit.jsx MC41). O próprio CadernoCockpit reescreve `tab`
    // segundos depois, assim que decide a aba inicial de verdade.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('workspaceId', workspaceId);
      next.delete('tab');
      return next;
    });
  }, [applyWorkspace, setSearchParams]);

  // Fecha o caderno aberto e volta pra lista — usado tanto pelo "Trocar"
  // (CadernoCockpit → onSwitchWorkspace(null)) quanto por qualquer fluxo
  // futuro equivalente. Espelha `openWorkspace`: limpa o estado E a URL
  // juntos, nunca um sem o outro (senão volta a divergir e o F5/Voltar
  // quebram de novo).
  const closeWorkspace = useCallback(() => {
    setActiveWorkspace(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('workspaceId');
      next.delete('tab');
      return next;
    });
  }, [setSearchParams]);

  // MC (15/09/2026, pedido do titular): sincroniza `activeWorkspace` A
  // PARTIR da URL (não só no mount — em QUALQUER mudança de
  // `?workspaceId=`, inclusive as que o próprio navegador faz sozinho ao
  // apertar Voltar/Avançar via popstate, que o react-router repassa pra
  // `searchParams`). Antes só existia o sentido contrário (abrir escrevia
  // a URL) e só rodava 1x no mount (`if (activeWorkspace) return` no topo
  // bloqueava qualquer atualização depois disso) — por isso Voltar não
  // fechava o caderno: nada reagia à URL ter perdido o `workspaceId`.
  // Chama `applyWorkspace` (não `openWorkspace`) de propósito — a URL já
  // está correta (foi o navegador que mudou, não um clique daqui), reescrevê-la
  // de novo empurraria uma entrada duplicada e faria o titular precisar
  // apertar Voltar 2x pra cada 1x (reintroduzindo o bug original).
  useEffect(() => {
    if (!Array.isArray(workspaces) || workspaces.length === 0) return;

    const targetId = String(searchParams.get('workspaceId') || '').trim();
    const currentId = String(activeWorkspace?.id || '').trim();
    if (targetId === currentId) return;

    if (!targetId) {
      setActiveWorkspace(null);
      // Achado ao vivo (Chrome real + CDP): fechar via Voltar do navegador
      // (popstate) podia deixar um `tab=` órfão na URL da lista (o
      // `replace` que o CadernoCockpit fazia pra refletir a aba ficava
      // "gravado" na entrada de histórico ANTERIOR à abertura do caderno,
      // então Voltar reaparecia com ele). Sem efeito funcional (a lista não
      // lê `tab`), mas suja a barra de endereço — replace silencioso
      // (nunca push, não é uma navegação de verdade) limpa isso.
      if (searchParams.get('tab')) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('tab');
            return next;
          },
          { replace: true },
        );
      }
      return;
    }

    const found = workspaces.find((w) => String(w?.id || '').trim() === targetId);
    if (!found) return;
    applyWorkspace(found);
  }, [workspaces, searchParams, activeWorkspace, applyWorkspace, setSearchParams]);

  useEffect(() => {
    if (activeWorkspace) return;
    if (!Array.isArray(workspaces) || workspaces.length === 0) return;
    if (!window?.Word) return;

    if (suppressWordAutoRestoreRef.current) return;
    let lastId;
    try {
      lastId = String(localStorage.getItem(LS_WORD_ACTIVE_AUTOS_WORKSPACE) || '').trim();
    } catch {
      lastId = '';
    }
    if (!lastId) return;
    const found = workspaces.find((w) => String(w?.id || '').trim() === lastId);
    if (!found) return;
    openWorkspace(found);
  }, [workspaces, activeWorkspace, openWorkspace]);

  const handleSelectClientFromCrm = useCallback((client) => {
    if (!client) return;
    const normalizedType = String(client?.type || '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const isAnimal = normalizedType.includes('animal');
    const name = isAnimal
      ? String(
          client?.qualification?.animal?.name ||
          client?.name ||
          'Animal'
        ).trim()
      : String(
          client?.qualification?.physical?.socialName ||
          client?.qualification?.physical?.name ||
          client?.qualification?.legal?.companyName ||
          client?.qualification?.legal?.company_name ||
          client?.anchor?.name ||
          client?.name ||
          'Cliente'
        ).trim();

    const resp = String(
      client?.qualification?.animal?.responsible?.name ||
      client?.qualification?.animal?.guardian?.name ||
      client?.qualification?.legal?.representative?.name ||
      client?.qualification?.minor?.representative?.name ||
      client?.anchor?.name ||
      ''
    ).trim();
    const safeId = String(client?.id || client?.uid || client?.key || client?.clientId || '').trim();
    if (!safeId) console.warn('[Autos] Alerta: Cliente selecionado não possui ID válido:', client);
    setNewClient({
      id: safeId,
      name: String(name || 'Cliente'),
      resp,
      type: String(client?.type || '').trim(),
      driveFolderId: String(client?.driveFolders?.processos || client?.drive?.processosFolderId || client?.driveFolderId || client?.drive?.rootFolderId || '').trim(),
      rootDriveFolderId: String(client?.driveFolders?.root || client?.driveFolderId || client?.drive?.rootFolderId || '').trim(),
      driveFolders: client?.driveFolders || null,
      driveTreeVersion: client?.driveTreeVersion || null,
      driveTreeHealthy: client?.driveTreeHealthy !== false,
      driveTreeLastCheckedAt: client?.driveTreeLastCheckedAt || null,
    });

    setIsClientPickerOpen(false);
  }, []);

  useEffect(() => {
    if (!Array.isArray(workspaces) || workspaces.length === 0) return;
    const now      = Date.now();
    const limit48h = now + 48 * 60 * 60 * 1000;
    const fired    = (() => { try { return new Set(JSON.parse(localStorage.getItem('rda_deadline_fired') || '[]')); } catch { return new Set(); } })();
    let changed = false;
    for (const w of workspaces) {
      const due = w?.dueDate;
      if (!due) continue;
      const dueMs = typeof due?.toDate === 'function' ? due.toDate().getTime() : new Date(due).getTime();
      if (!Number.isFinite(dueMs) || dueMs < now || dueMs > limit48h) continue;
      const key = String(w.id || '');
      if (fired.has(key)) continue;
      fired.add(key);
      changed = true;
      const label = String(w?.pieceType || w?.id || 'Caderno');
      const npu   = String(w?.ancestry?.primary_process_id || '').trim();
      createOfficeNotification(
        `⚠️ Prazo Fatal Iminente`,
        `O caderno "${label}"${npu ? ` (Processo ${npu})` : ''} vence em menos de 48 horas.`,
        'DEADLINE',
        null
      );
    }
    if (changed) {
      try { localStorage.setItem('rda_deadline_fired', JSON.stringify([...fired])); } catch {}
    }
  }, [workspaces]);

  const linkingWorkspaceMemo = useMemo(
    () => (linkingWorkspaceId ? workspaces.find((w) => String(w?.id) === linkingWorkspaceId) || null : null),
    [linkingWorkspaceId, workspaces]
  );
  const linkingExistingPids = useMemo(() => {
    if (!linkingWorkspaceMemo) return [];
    const cid = String(linkingWorkspaceMemo?.clientId || '').trim();
    if (!cid) return [];
    const pids = new Set();
    for (const w of workspaces) {
      if (String(w?.id) === linkingWorkspaceId) continue;
      if (String(w?.clientId || '').trim() !== cid) continue;
      const pid = String(w?.ancestry?.primary_process_id || '').trim();
      if (pid) pids.add(pid);
    }
    return Array.from(pids).sort();
  }, [workspaces, linkingWorkspaceMemo, linkingWorkspaceId]);

  const existingProcessIds = useMemo(() => {
    const cid = String(newClient?.id || '').trim();
    if (!cid) return [];
    const pids = new Set();
    for (const w of workspaces) {
      const wCid = String(w?.clientId || '').trim();
      const inParties = Array.isArray(w?.partiesRefs) && w.partiesRefs.some((p) => String(p?.clientId || '').trim() === cid);
      if (wCid !== cid && !inParties) continue;
      const pid = String(w?.ancestry?.primary_process_id || '').trim();
      if (pid) pids.add(pid);
    }
    return Array.from(pids).sort();
  }, [workspaces, newClient]);

  const resetNewModal = useCallback(() => {
    setNewClient(null);
    setNewPieceType('Petição Inicial (Cível)');
    setNewProcessMode('existing');
    setNewPrimaryProcessId('');
    setNewJurisdiction([]);
    setNewJurisdictionOpen(false);
    setNewJuizo('');
    setNewComarca('');
    setNewStatus('Ativo');
  }, []);

  const handleCreateWorkspace = useCallback(async () => {
    // FIX DUPLO-SUBMIT (20/08/2026): sem esta trava, Enter repetido no modal
    // (ex.: digitando em campo de busca do cliente e apertando Enter por hábito)
    // ou duplo-clique em "Criar" disparavam handleCreateWorkspace() várias vezes
    // em paralelo — cada chamada corria /drive/create-client-folder de forma
    // concorrente, criando pastas duplicadas no Drive ("Caderno de Autos (1)",
    // "(2)"... caso real em 16/08/2026).
    if (creatingWorkspaceRef.current) return;
    creatingWorkspaceRef.current = true;
    setIsCreatingWorkspace(true);
    setError(null);
    setNotice(null);

    if (window?.Word) {
      setError('No Word, o Organizador funciona como espelho (somente leitura). Crie/edite cadernos pelo Navegador.');
      creatingWorkspaceRef.current = false;
      setIsCreatingWorkspace(false);
      return;
    }

    const clientId = String(newClient?.id || '').trim();
    const clientName = String(newClient?.name || '').trim();
    const pieceType = String(newPieceType || '').trim();

    if (!clientId || !clientName) {
      setError('Selecione um cliente (CRM) antes de criar o caderno.');
      creatingWorkspaceRef.current = false;
      setIsCreatingWorkspace(false);
      return;
    }
    if (!pieceType) {
      setError('Selecione o tipo de peça antes de criar o caderno.');
      creatingWorkspaceRef.current = false;
      setIsCreatingWorkspace(false);
      return;
    }

    try {
      let resolvedDriveFolders = newClient?.driveFolders || null;
      let resolvedProcessFolderId = readText(newClient?.driveFolders?.processos || newClient?.driveFolderId);
      const rootDriveFolderId = readText(newClient?.rootDriveFolderId || newClient?.driveFolders?.root || newClient?.driveFolderId);
      const baseUrl = getBackendBaseUrl();

      if (!baseUrl) {
        throw new Error('Backend não configurado para orquestrar a pasta do cliente no Drive.');
      }

      const _doCreateFolder = () => fetch(`${baseUrl}/drive/create-client-folder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, clientName, requesterEmail: '' }),
      });
      let createFolderResp = await _doCreateFolder();
      let createFolderPayload = await createFolderResp.json().catch(() => ({}));

      // [P76] Self-healing: pasta raiz órfã (422) → purga campos do Firestore e tenta novamente.
      if (createFolderResp.status === 422 && clientId) {
        await clearClientOrphanDriveFields(clientId);
        createFolderResp = await _doCreateFolder();
        createFolderPayload = await createFolderResp.json().catch(() => ({}));
      }

      if (!createFolderResp.ok) {
        throw new Error(String(createFolderPayload?.detail || createFolderPayload?.error || 'Falha ao orquestrar a pasta do cliente no Google Drive.'));
      }

      const newRootFolderId = readText(createFolderPayload?.driveFolderId || rootDriveFolderId);
      resolvedDriveFolders = createFolderPayload?.driveFolders && typeof createFolderPayload.driveFolders === 'object'
        ? createFolderPayload.driveFolders
        : resolvedDriveFolders;
      resolvedProcessFolderId = readText(resolvedDriveFolders?.processos || resolvedProcessFolderId);

      if (clientId && newRootFolderId) {
        await updateClientDriveInfo(clientId, {
          driveFolderId: newRootFolderId,
          driveFolders: resolvedDriveFolders || null,
          drive: resolvedDriveFolders ? {
            rootFolderId: readText(resolvedDriveFolders?.root || newRootFolderId),
            triagemFolderId: readText(resolvedDriveFolders?.triagem),
            contratosFolderId: readText(resolvedDriveFolders?.contratos),
            whatsappFolderId: readText(resolvedDriveFolders?.whatsapp),
            processosFolderId: readText(resolvedDriveFolders?.processos),
          } : null,
          driveTreeVersion: readText(createFolderPayload?.driveTreeVersion) || null,
          driveTreeChanges: Array.isArray(createFolderPayload?.driveTreeChanges) ? createFolderPayload.driveTreeChanges : [],
          driveTreeHealthy: true,
          driveTreeLastCheckedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      if (!resolvedProcessFolderId) {
        throw new Error('A subpasta estrutural Caderno de Autos não foi retornada pelo Drive. Verifique as permissões do backend.');
      }

      const workspaceSeedRef = createNewWorkspaceRef();

      // FIX ESTRUTURA CADERNO > PEÇAS (21/08/2026): um caderno representa um
      // processo, e um processo pode ter várias peças (Denúncia, Resposta à
      // Acusação, Apelação...). Antes, o caderno só tinha UMA pasta no Drive
      // (a "pasta da peça"), então cada peça nova reaproveitava e renomeava
      // essa mesma pasta — parecia que uma peça "sobrescrevia" a outra.
      // Agora: 1º cria a pasta do CADERNO (o processo em si), depois cria a
      // pasta da PRIMEIRA peça DENTRO dela. Peças seguintes ("+ Nova Peça")
      // ganham cada uma sua própria pasta, também dentro da pasta do caderno.
      // FIX NOME DO CADERNO (21/08/2026): NUNCA cair de volta no pieceType
      // aqui — se o caderno não tem NPU nem apelido (caso pré-processual), a
      // pasta do caderno ficava com o MESMO nome da pasta da primeira peça
      // dentro dela ("2026-08-21 - Petição Inicial (Cível)" nos dois
      // níveis), dando a impressão de que não existia pasta do caderno, só a
      // peça direto dentro de "Caderno de Autos". Mesmo fallback usado pelo
      // rótulo do caderno no menu lateral (resolveCadernoLabel em
      // CadernoCockpit.jsx): NPU → apelido → "Cliente (Pré-Processual)".
      const cadernoLabel = (() => {
        const pid = newProcessMode !== 'alias' ? String(newPrimaryProcessId || '').trim() : '';
        if (pid) return formatNPU(pid) || pid;
        const alias = newProcessMode === 'alias' ? String(newPrimaryProcessId || '').trim() : '';
        if (alias) return alias;
        return `${clientName} (Pré-Processual)`;
      })();
      const cadernoFolderResp = await fetch(`${baseUrl}/drive/ensure-workspace-folders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          processRootFolderId: resolvedProcessFolderId,
          workspaceId: workspaceSeedRef.id,
          pieceType: cadernoLabel,
          clientId,
          clientName,
          includeDatePrefix: false,
        }),
      });
      const cadernoFolderPayload = await cadernoFolderResp.json().catch(() => ({}));
      if (!cadernoFolderResp.ok) {
        throw new Error(String(cadernoFolderPayload?.detail || cadernoFolderPayload?.error || 'Falha ao criar a pasta do caderno no Drive.'));
      }
      const cadernoFolderId = readText(cadernoFolderPayload?.pieceFolder?.id);
      if (!cadernoFolderId) {
        throw new Error('Backend não retornou a pasta do caderno.');
      }
      // /drive/ensure-workspace-folders sempre cria uma subpasta "Anexos"
      // junto -- útil quando ela é a pasta de uma PEÇA, mas aqui ela é a
      // pasta do CADERNO (container), que não usa Anexos próprios (cada
      // peça tem o seu). Apaga essa sobra pra não confundir no Drive.
      const cadernoAnexosOrphanId = readText(cadernoFolderPayload?.attachmentsFolder?.id);
      if (cadernoAnexosOrphanId) {
        fetch(`${baseUrl}/drive/delete-folder`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ folderId: cadernoAnexosOrphanId }),
        }).catch(() => {});
      }

      const ensureResp = await fetch(`${baseUrl}/drive/ensure-workspace-folders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          processRootFolderId: cadernoFolderId,
          workspaceId: workspaceSeedRef.id,
          pieceType,
          clientId,
          clientName,
          includeDatePrefix: false,
        }),
      });
      const ensurePayload = await ensureResp.json().catch(() => ({}));
      if (!ensureResp.ok) {
        throw new Error(String(ensurePayload?.detail || ensurePayload?.error || 'Falha ao garantir a subárvore do caderno de autos.'));
      }

      const processPieceFolderId = readText(ensurePayload?.pieceFolder?.id);
      const attachmentsFolderId = readText(ensurePayload?.attachmentsFolder?.id);
      if (!processPieceFolderId || !attachmentsFolderId) {
        throw new Error('Backend não retornou a pasta da peça e a subpasta Anexos para o caderno.');
      }

      const initialParty = {
        clientId,
        role: (() => {
          const nt = String(newClient?.type || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
          return nt.includes('animal') ? 'Animal' : 'Autor';
        })(),
        name: clientName,
        resp: String(newClient?.resp || '').trim(),
      };
      const resolvedPid = newProcessMode === 'alias' ? null : (String(newPrimaryProcessId || '').trim() || null);
      const preProcessualNote = newProcessMode === 'alias' ? String(newPrimaryProcessId || '').trim() || null : null;
      // MC1 SSOT V3: usar resolveCadernoDocumentosV3 como fonte primária da taxonomia
      const _v3 = resolveCadernoDocumentosV3(pieceType);
      const initialChecklist = _v3.length > 0 ? _v3.map((d) => d.label) : (CHECKLISTS[pieceType] || []);
      const initialSlotOrder = initialChecklist.map((_, i) => `slot-${i}`);
      const initialSlotNames = {};
      const initialClientReq = {};
      initialSlotOrder.forEach((sid, idx) => {
        initialSlotNames[sid] = initialChecklist[idx];
        initialClientReq[sid] = false;
      });
      const firstPieceId = `piece-${Date.now()}`;
      const payload = {
        clientId,
        clientName,
        pieceType,
        partiesRefs: [initialParty],
        driveFolderInput: attachmentsFolderId || null,
        driveFolderId: attachmentsFolderId || null,
        driveFolders: resolvedDriveFolders || null,
        processRootFolderId: resolvedProcessFolderId || null,
        cadernoFolderId: cadernoFolderId || null,
        processPieceFolderId: processPieceFolderId || null,
        processPieceFolderName: readText(ensurePayload?.pieceFolder?.name) || null,
        attachmentsFolderId: attachmentsFolderId || null,
        attachmentsFolderName: readText(ensurePayload?.attachmentsFolder?.name) || 'Anexos',
        pieces: [{
          id: firstPieceId,
          pieceType,
          processPieceFolderId: processPieceFolderId || null,
          processPieceFolderName: readText(ensurePayload?.pieceFolder?.name) || null,
          attachmentsFolderId: attachmentsFolderId || null,
          attachmentsFolderName: readText(ensurePayload?.attachmentsFolder?.name) || 'Anexos',
        }],
        juizo: String(newJuizo || '').trim() || null,
        comarca: String(newComarca || '').trim() || null,
        // Fusão Processos/Caderno (MC08): numeroCNJ como campo de 1ª classe,
        // reaproveitando o mesmo valor já capturado pelo fluxo existente
        // (resolvedPid) — não é um campo de formulário novo, só passa a ser
        // persistido também fora de `ancestry`, que continua intacto.
        numeroCNJ: resolvedPid,
        // Fusão Processos/Caderno (MC48, 22/08/2026): completa o MC09 —
        // agora tem seletor no formulário (abaixo); só cai em 'Ativo' se o
        // operador não mudar nada. A edição via Kanban (drag-and-drop)
        // continua funcionando via saveStatus, sem alteração.
        status: newStatus || 'Ativo',
        ancestry: {
          client_id: clientId || null,
          primary_process_id: resolvedPid,
          pre_processual_note: preProcessualNote || null,
          jurisdiction_branch: newJurisdiction.length > 0 ? newJurisdiction : null,
          depth_level: 1,
        },
        organizerState: initialSlotOrder.length ? {
          assignments: {},
          slotNames: initialSlotNames,
          slotOrder: initialSlotOrder,
          clientRequested: initialClientReq,
        } : null,
        organizerStateUpdatedAtMs: initialSlotOrder.length ? Date.now() : null,
        caseTeam: [{ ...TITULAR_SEED, addedAt: new Date().toISOString() }],
        caseTeamIds: [TITULAR_SEED.clientId],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await createWorkspace(workspaceSeedRef, payload);
      setNotice('Caderno criado.');
      setIsNewOpen(false);
      resetNewModal();
      setActiveWorkspace({ id: workspaceSeedRef.id, ...payload });
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      creatingWorkspaceRef.current = false;
      setIsCreatingWorkspace(false);
    }
  }, [newClient, newPieceType, newPrimaryProcessId, newProcessMode, newJurisdiction, newJuizo, newComarca, newStatus, resetNewModal]);

  const handleDeleteWorkspace = useCallback(async (workspaceId) => {
    if (!workspaceId) return;
    try {
      await deleteWorkspace(workspaceId);
      setDeletingWorkspaceId('');
      setNotice('Caderno excluído.');
    } catch (e) {
      setError(String(e?.message || e));
      setDeletingWorkspaceId('');
    }
  }, []);

  const handleNukeClientWorkspaces = useCallback(async (group) => {
    const count = group.length;
    if (!count) return;
    const ok1 = window.confirm(`ATENÇÃO: Isso apagará TODOS os ${count} caderno${count !== 1 ? 's' : ''} deste cliente. Esta ação é irreversível. Deseja continuar?`);
    if (!ok1) return;
    const ok2 = window.confirm(`CONFIRMAÇÃO FINAL: ${count} caderno${count !== 1 ? 's' : ''} serão deletados permanentemente do Firestore. Tem certeza absoluta?`);
    if (!ok2) return;
    try {
      let driveFailCount = 0;
      const baseUrl = getBackendBaseUrl();
      if (baseUrl) {
        setNukeProgress('Limpando Drive...');
        const token = await getToken().catch(() => null);
        const driveIds = [...new Set(
          group.flatMap((w) => [
            String(w?.processPieceFolderId || ''),
            String(w?.attachmentsFolderId || ''),
            String(w?.driveFolderId || ''),
          ]).filter((id) => id && id !== 'undefined' && id !== 'null' && id.trim() !== '')
        )];
        const driveResults = await Promise.allSettled(
          driveIds.map((folderId) =>
            fetch(`${baseUrl}/drive/delete-folder`, {
              method: 'DELETE',
              headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
              body: JSON.stringify({ folderId }),
            }).then(async (res) => {
              const data = await res.json().catch(() => ({}));
              if (!res.ok || !data?.ok) throw new Error(String(data?.detail || data?.error || 'Drive API delete failed'));
              return data;
            })
          )
        );
        driveFailCount = driveResults.filter((r) => r.status === 'rejected').length;
      }
      if (driveFailCount > 0) {
        setNukeProgress('');
        setError(`⚠️ Falha ao apagar ${driveFailCount} pasta${driveFailCount !== 1 ? 's' : ''} no Google Drive. Registo mantido no banco de dados. Verifique as permissões do Drive e tente novamente.`);
        return;
      }
      setNukeProgress('Limpando Banco...');
      await deleteWorkspaces(group.map((w) => String(w?.id || '')));
      setNukeProgress('');
      setNotice(`☢️ ${count} caderno${count !== 1 ? 's' : ''} apagado${count !== 1 ? 's' : ''} completamente (Banco + Drive).`);
    } catch (e) {
      setNukeProgress('');
      setError(String(e?.message || e));
    }
  }, []);

  const handleLegacyDriveClean = useCallback(async () => {
    const ids = legacyInput
      .split(/[\n,;]+/)
      .map((s) => {
        const trimmed = s.trim();
        const match = trimmed.match(/\/folders\/([a-zA-Z0-9_-]+)/);
        return match ? match[1] : trimmed;
      })
      .filter(Boolean);
    if (!ids.length) return;
    setLegacyBusy(true);
    setLegacyResult(null);
    try {
      const baseUrl = getBackendBaseUrl();
      if (!baseUrl) throw new Error('Falha de Conexão: Não foi possível comunicar com os servidores RDA na Nuvem.');
      const token = await getToken().catch(() => null);
      const results = await Promise.allSettled(
        ids.map((folderId) =>
          fetch(`${baseUrl}/drive/delete-folder`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({ folderId }),
          })
        )
      );
      const ok   = results.filter((r) => r.status === 'fulfilled' && r.value?.ok).length;
      const fail = results.length - ok;
      setLegacyResult({ ok, fail, total: ids.length });
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLegacyBusy(false);
    }
  }, [legacyInput]);

  const handleAutoWipe = useCallback(async () => {
    const count = workspaces.length;
    if (!count) return;
    const ok1 = window.confirm(`☢️ RESET TOTAL\n\nSerão apagados:\n• ${count} caderno(s) do Firestore\n• Todas as pastas vinculadas do Google Drive\n\nEsta ação é IRREVERSÍVEL. Tem certeza absoluta?`);
    if (!ok1) return;
    setLegacyBusy(true);
    setLegacyResult(null);
    try {
      const baseUrl = getBackendBaseUrl();
      const token   = await getToken().catch(() => null);
      const driveIds = [...new Set(
        workspaces.flatMap((w) => [
          String(w?.processPieceFolderId || ''),
          String(w?.attachmentsFolderId  || ''),
          String(w?.driveFolderId        || ''),
        ]).filter((id) => id && id !== 'undefined' && id !== 'null' && id.trim() !== '')
      )];
      let driveOk = 0, driveFail = 0;
      if (baseUrl && driveIds.length) {
        const driveResults = await Promise.allSettled(
          driveIds.map((folderId) =>
            fetch(`${baseUrl}/drive/delete-folder`, {
              method: 'DELETE',
              headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
              body: JSON.stringify({ folderId }),
            })
          )
        );
        driveOk   = driveResults.filter((r) => r.status === 'fulfilled' && r.value?.ok).length;
        driveFail = driveResults.length - driveOk;
      }
      if (driveFail > 0) {
        setLegacyResult({ ok: driveOk, fail: driveFail, total: driveIds.length, fsDeleted: 0 });
        setError(`⚠️ Padrão SAGA: ${driveFail} pasta${driveFail !== 1 ? 's' : ''} não pôde${driveFail !== 1 ? 'ram' : ''} ser apagada${driveFail !== 1 ? 's' : ''} no Google Drive. Os registros foram MANTIDOS no banco de dados para evitar "pastas zumbi". Verifique as permissões da Service Account e tente novamente.`);
        return;
      }
      await deleteWorkspaces(workspaces.map((w) => String(w?.id || '')));
      setLegacyResult({ ok: driveOk, fail: driveFail, total: driveIds.length, fsDeleted: count });
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLegacyBusy(false);
    }
  }, [workspaces]);

  const handleLinkWorkspace = useCallback(async () => {
    if (!linkingWorkspaceId || !linkingNpu) return;
    if (!isValidNPU(linkingNpu)) return;
    try {
      const current = workspaces.find((w) => String(w?.id) === linkingWorkspaceId);
      const currentAncestry = current?.ancestry || {};
      await linkWorkspaceToProcess(linkingWorkspaceId, {
        // Fusão Processos/Caderno (MC08): grava numeroCNJ junto de
        // ancestry.primary_process_id — este é o fluxo que resolveria os 3
        // workspaces órfãos do caso [PET_A] achados na auditoria, se o
        // titular relinkar por aqui.
        numeroCNJ: linkingNpu,
        ancestry: { ...currentAncestry, primary_process_id: linkingNpu },
        updatedAt: serverTimestamp(),
      });
      setNotice(`Caderno vinculado ao processo ${formatNPU(linkingNpu) || linkingNpu}.`);
      setLinkingWorkspaceId('');
      setLinkingNpu('');
    } catch (e) {
      setError(String(e?.message || e));
    }
  }, [linkingWorkspaceId, linkingNpu, workspaces]);


  useEffect(() => {
    if (!isNewOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsNewOpen(false);
        resetNewModal();
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        const tag = document.activeElement?.tagName || '';
        if (tag === 'TEXTAREA' || tag === 'SELECT') return;
        if (document.activeElement?.closest('button, a, [role="button"]')) return;
        e.preventDefault();
        handleCreateWorkspace();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [isNewOpen, resetNewModal, handleCreateWorkspace]);

  // MC_X (Extermínio da Tela Zero): IDE sempre ativo — nunca mais retorna lista de cards.
  const handleRequestNewCaderno = (clientHint) => {
    if (clientHint && typeof clientHint === 'object') {
      setNewClient(clientHint);
    } else if (typeof clientHint === 'string' && clientHint) {
      const ws = (workspaces || []).find((w) => String(w?.clientId || '').trim() === String(clientHint).trim());
      if (ws) {
        setNewClient({
          id: ws.clientId || null,
          name: ws.clientName || '',
          driveFolderId: ws.driveFolderId || null,
        });
      }
    }
    setIsNewOpen(true);
  };

  // MC_X: cadernos-irmãos do cliente ativo (pré-computado para o Cockpit).
  useEffect(() => {
    if (!activeWorkspace?.clientId) return;
    const cid = String(activeWorkspace.clientId).trim();
    if (!cid || !workspaces.length) return;
    // FIX (22/08/2026, achado [PET_A]): passar a lista JÁ FILTRADA por
    // clientId escondia workspaces com clientId vazio de qualquer checagem
    // — nenhum filtro `=== cid` os inclui, pra nenhum cliente. A função já
    // filtra por dentro pras regras 1/2; a regra 0 (clientId vazio) precisa
    // da lista inteira pra não ficar cega a esse tipo de dado corrompido.
    checkMasterIntegrity({ workspaces, clientId: cid });
  }, [activeWorkspace?.clientId, workspaces]);

  const activeClientKeyMC = String(activeWorkspace?.clientId || activeWorkspace?.clientName || '').trim().toUpperCase();
  const clientWorkspacesMC = activeWorkspace
    ? (workspaces || []).filter((w) => {
        const k = String(w?.clientId || w?.clientName || '').trim().toUpperCase();
        return k && k === activeClientKeyMC;
      })
    : [];

  return (
    <>
      {!activeWorkspace ? (
        // Fusão Processos/Caderno (MC16b): lista global entra ANTES de abrir
        // um caderno específico. Não toca em CadernoCockpit.jsx — quando um
        // workspace é selecionado (aqui ou pelo fluxo de criação já
        // existente), o mesmo <CadernoCockpit> de sempre assume, inalterado.
        <ProcessosCadernoListView
          workspaces={workspaces || []}
          onSelectWorkspace={(w) => openWorkspace(w)}
          onCreateNew={() => handleRequestNewCaderno()}
        />
      ) : (
      <CadernoCockpit
        activeWorkspace={activeWorkspace}
        initialFolderId={activeWorkspace?.attachmentsFolderId || activeWorkspace?.driveFolderId}
        autoConnect={true}
        allWorkspaces={workspaces || []}
        clientWorkspaces={clientWorkspacesMC}
        onSwitchWorkspace={(w) => {
          // MC_Y7: aceita null para limpar workspace stale quando o cliente
          // selecionado não possui nenhum caderno (ex.: novo cliente no CRM).
          suppressWordAutoRestoreRef.current = true;
          if (w) {
            // MC_Y8: roteia pelo openWorkspace para garantir ensureWorkspaceDriveReady
            // (resolve pastas Drive antes de ativar). Evita "Falha de conexão com o Drive".
            openWorkspace(w);
          } else {
            closeWorkspace();
          }
        }}
        onRequestNewCaderno={handleRequestNewCaderno}
        onRequestLinkProcess={(wid) => setLinkingWorkspaceId(String(wid || ''))}
        onRequestDeleteWorkspace={(wid) => setDeletingWorkspaceId(String(wid || ''))}
        onRequestLegacyCleanup={() => { setLegacyInput(''); setLegacyResult(null); setLegacyOpen(true); }}
      />
      )}

      {/* Notificações globais (toast top-right) */}
      {error ? (<div className="fixed top-3 right-3 z-[9998] max-w-md p-3 rounded-lg border border-red-200 bg-red-50 text-xs text-red-700 whitespace-pre-wrap shadow-lg">{error}</div>) : null}
      {nukeProgress ? (<div className="fixed top-3 right-3 z-[9998] max-w-md p-3 rounded-lg border border-blue-200 bg-blue-50 text-xs text-blue-800 font-bold animate-pulse flex items-center gap-2 shadow-lg"><span>⚙️</span>{nukeProgress}</div>) : null}
      {notice ? (<div className="fixed top-3 right-3 z-[9998] max-w-md p-3 rounded-lg border border-green-200 bg-green-50 text-xs text-green-800 shadow-lg">{notice}</div>) : null}

      {isNewOpen ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-xl bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200">

            <div className="bg-[#20243B] p-4 text-white">
              <h3 className="font-extrabold text-sm uppercase tracking-widest text-[#C5A059]">Novo Processo/Caso</h3>
              <div className="text-[11px] text-slate-300 mt-0.5">Selecione o cliente, o processo e o tipo de peça.</div>
            </div>

            <div className="p-5 grid grid-cols-1 gap-4 overflow-y-auto max-h-[65vh]">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1">Cliente</label>
                {newClient?.id ? (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 px-3 py-2.5 rounded-lg border-2 border-emerald-300 bg-emerald-50 text-sm font-semibold text-[#20243B] truncate">
                      <i className="fas fa-user text-[10px] text-[#C5A059] mr-1.5"></i>{toTitleCasePt(newClient.name) || 'Cliente selecionado'}
                    </div>
                    <button type="button" onClick={() => setIsClientPickerOpen(true)}
                      className="px-2.5 py-2.5 rounded-lg border-2 border-gray-300 bg-white text-[9px] font-black uppercase tracking-widest text-gray-500 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors shrink-0">
                      Trocar
                    </button>
                  </div>
                ) : (
                  <input
                    value={toTitleCasePt(newClient?.name)}
                    readOnly
                    onClick={() => setIsClientPickerOpen(true)}
                    onFocus={() => setIsClientPickerOpen(true)}
                    placeholder="Clique aqui para buscar cliente no CRM..."
                    className="w-full px-3 py-2.5 rounded-lg border-2 border-[#C5A059] bg-white text-sm cursor-pointer hover:border-[#20243B] focus:outline-none"
                  />
                )}
              </div>

              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1">Processo / Origem</label>
                <div className="flex gap-1 mb-2">
                  {[['existing', 'Existente'], ['new', 'Novo NPU'], ['alias', 'Pré-Processual']].map(([mode, label]) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => { setNewProcessMode(mode); setNewPrimaryProcessId(''); }}
                      className={`flex-1 px-2 py-1 rounded text-[9px] font-black uppercase tracking-wider border transition ${
                        newProcessMode === mode
                          ? 'border-[#C5A059] bg-[#C5A059]/10 text-[#20243B]'
                          : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                      }`}
                    >{label}</button>
                  ))}
                </div>
                {newProcessMode === 'existing' && (
                  existingProcessIds.length > 0 ? (
                    <select
                      value={newPrimaryProcessId}
                      onChange={(e) => setNewPrimaryProcessId(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border-2 border-gray-300 bg-white text-sm"
                    >
                      <option value="">(Sem processo vinculado)</option>
                      {existingProcessIds.map((pid) => <option key={pid} value={pid}>{pid}</option>)}
                    </select>
                  ) : (
                    <p className="text-[10px] text-gray-400 italic px-1">Nenhum processo existente para este cliente. Crie um novo acima.</p>
                  )
                )}
                {newProcessMode === 'new' && (
                  <div>
                    <input
                      value={newPrimaryProcessId}
                      onChange={(e) => setNewPrimaryProcessId(formatNPU(e.target.value))}
                      placeholder="0000000-00.0000.0.00.0000"
                      maxLength={25}
                      className={`w-full px-3 py-2 rounded-lg border-2 font-mono text-sm ${
                        newPrimaryProcessId && !isValidNPU(newPrimaryProcessId)
                          ? 'border-red-400 bg-red-50'
                          : 'border-gray-300 bg-white'
                      }`}
                    />
                    {newPrimaryProcessId && !isValidNPU(newPrimaryProcessId) && (
                      <p className="text-[10px] text-red-600 mt-0.5 px-1">Formato inválido — CNJ: NNNNNNN-DD.AAAA.J.TT.OOOO</p>
                    )}
                  </div>
                )}
                {newProcessMode === 'alias' && (
                  <div>
                    <input
                      value={newPrimaryProcessId}
                      onChange={(e) => setNewPrimaryProcessId(e.target.value)}
                      placeholder="Ex: Caso Indenizatório João Silva — Pré-Judicial"
                      maxLength={120}
                      className="w-full px-3 py-2 rounded-lg border-2 border-gray-300 bg-white text-sm"
                    />
                    <p className="text-[9px] text-gray-400 mt-0.5 px-1">Memo interno — Não é um NPU. O caderno será criado sem vinculação processual (primary_process_id: null).</p>
                  </div>
                )}
              </div>

              <div className="relative" ref={newJurisdictionRef}>
                <label className="block text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1">Ramo do Direito (CNJ)</label>
                <button
                  type="button"
                  onClick={() => setNewJurisdictionOpen((p) => !p)}
                  className="w-full px-3 py-2 rounded-lg border-2 border-gray-300 bg-white text-sm text-left flex items-center justify-between gap-2"
                >
                  <span className="truncate text-slate-700">
                    {newJurisdiction.length === 0
                      ? 'Não especificado'
                      : newJurisdiction.map((v) => JURISDICTION_OPTIONS.find((o) => o.value === v)?.label || v).join(', ')}
                  </span>
                  <span className="text-slate-400 text-xs shrink-0">{newJurisdictionOpen ? '▲' : '▼'}</span>
                </button>
                {newJurisdictionOpen && (
                  <div className="absolute z-50 mt-1 w-full max-h-56 overflow-auto rounded-lg border border-gray-200 bg-white shadow-xl">
                    {JURISDICTION_OPTIONS.map((opt) => {
                      const checked = newJurisdiction.includes(opt.value);
                      return (
                        <label
                          key={opt.value}
                          className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-50 text-sm ${checked ? 'bg-[#C5A059]/10 font-semibold text-[#20243B]' : 'text-slate-700'}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setNewJurisdiction((prev) =>
                                checked ? prev.filter((v) => v !== opt.value) : [...prev, opt.value]
                              );
                            }}
                            className="accent-[#C5A059] w-4 h-4"
                          />
                          {opt.label}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1">Tipo de Peça</label>
                <select
                  value={newPieceType}
                  onChange={(e) => setNewPieceType(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-300 bg-white text-sm"
                >
                  <optgroup label="★  Peças Principais">
                    {PINNED_PIECE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </optgroup>
                  <optgroup label="──  Demais Peças (A–Z)  ──">
                    {PIECE_TYPES.filter((t) => !PINNED_PIECE_TYPES.includes(t)).map((t) => <option key={t} value={t}>{t}</option>)}
                  </optgroup>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1">Juízo / Vara <span className="font-normal text-gray-400">(opcional)</span></label>
                  <input
                    value={newJuizo}
                    onChange={(e) => setNewJuizo(e.target.value)}
                    placeholder="Ex: 3ª Vara Cível"
                    maxLength={120}
                    className="w-full px-3 py-2 rounded-lg border-2 border-gray-300 bg-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1">Comarca / Foro <span className="font-normal text-gray-400">(opcional)</span></label>
                  <input
                    value={newComarca}
                    onChange={(e) => setNewComarca(e.target.value)}
                    placeholder="Ex: Curitiba/PR"
                    maxLength={120}
                    className="w-full px-3 py-2 rounded-lg border-2 border-gray-300 bg-white text-sm"
                  />
                </div>
              </div>

              <div>
                {/* MC48 (22/08/2026): mesma lista de status do Kanban
                    (STATUS_COLUMNS, ProcessosCadernoListView.jsx) — evita
                    criar caderno "Ativo" por padrão quando já nasce
                    suspenso/em recurso/arquivado. */}
                <label className="block text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1">Status</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-300 bg-white text-sm"
                >
                  {STATUS_COLUMNS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div className="p-4 bg-gray-50 border-t border-gray-100">
              <SaveCancelBar
                onCancel={() => { setIsNewOpen(false); resetNewModal(); }}
                onSave={handleCreateWorkspace}
                cancelLabel="Cancelar"
                saveLabel={isCreatingWorkspace ? 'Criando...' : 'Criar e Abrir'}
                saving={isCreatingWorkspace}
              />
            </div>

            {isClientPickerOpen ? (
              <CrmClientPickerModal
                isOpen={true}
                onClose={() => setIsClientPickerOpen(false)}
                onSelect={handleSelectClientFromCrm}
                db={db}
                appId={appId}
                title="Base de Clientes (CRM)"
              />
            ) : null}
          </div>
        </div>
      ) : null}

      {deletingWorkspaceId ? (() => {
        // MC60 (03/09/2026, incidente real: caderno inteiro apagado por
        // engano) — este modal só mostrava o ID cru do documento (sem
        // significado nenhum pra um humano) e apagava com 1 clique só,
        // sem digitar nada. Mostra agora o nome do cliente/CNJ de verdade
        // e exige digitar o nome do cliente pra habilitar o botão —
        // mesmo padrão já aplicado no "Excluir Caderno" de dentro do
        // caderno aberto.
        const alvo = (workspaces || []).find(
          (w) => String(w?.id || '') === String(deletingWorkspaceId || ''),
        );
        const nomeCliente = String(alvo?.clientName || '').trim();
        const cnj = String(alvo?.numeroCNJ || '').trim();
        const confirmAlvo = nomeCliente || String(deletingWorkspaceId || '');
        return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-sm bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200">
            <div className="bg-[#20243B] p-4 text-white">
              <h3 className="font-extrabold text-sm uppercase tracking-widest text-red-400">Excluir o CASO inteiro</h3>
              <div className="text-[11px] text-slate-300 mt-0.5">Esta ação é irreversível.</div>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-700 mb-2">
                Isso apaga <strong>TODO</strong> o caderno de{' '}
                <strong className="text-gray-900">{nomeCliente || 'este cliente'}</strong>
                {cnj ? <> (processo {cnj})</> : null} — todas as peças, todos os anexos, e as
                pastas correspondentes no Google Drive.
              </p>
              <p className="text-xs text-red-600 font-bold mt-3 mb-1">
                Digite <code className="bg-red-50 px-1 rounded">{confirmAlvo}</code> pra confirmar:
              </p>
              <input
                autoFocus
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder={confirmAlvo}
              />
            </div>
            <div className="p-4 bg-gray-50 border-t border-gray-100 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => { setDeletingWorkspaceId(''); setDeleteConfirmText(''); }}
                className="px-4 py-2 rounded-lg border border-gray-300 text-xs font-bold text-gray-700 hover:bg-gray-100"
              >Cancelar</button>
              <button
                type="button"
                disabled={deleteConfirmText.trim().toUpperCase() !== confirmAlvo.toUpperCase()}
                onClick={() => { handleDeleteWorkspace(deletingWorkspaceId); setDeleteConfirmText(''); }}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-black uppercase tracking-widest disabled:opacity-40"
              >Excluir o caso inteiro</button>
            </div>
          </div>
        </div>
        );
      })() : null}

      {linkingWorkspaceId ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200">
            <div className="bg-[#20243B] p-4 text-white">
              <h3 className="font-extrabold text-sm uppercase tracking-widest text-[#C5A059]">Vincular a Processo</h3>
              <div className="text-[11px] text-slate-300 mt-0.5">Migre este caderno para um processo existente ou novo NPU.</div>
            </div>
            <div className="p-5 grid grid-cols-1 gap-4">
              {linkingExistingPids.length > 0 && (
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1">Processos Existentes do Cliente</label>
                  <select
                    value={linkingNpu}
                    onChange={(e) => setLinkingNpu(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border-2 border-gray-300 bg-white text-sm"
                  >
                    <option value="">(Digitar manualmente abaixo)</option>
                    {linkingExistingPids.map((pid) => (
                      <option key={pid} value={pid}>{formatNPU(pid) || pid}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1">NPU — Número Único do Processo (CNJ)</label>
                <input
                  value={linkingNpu}
                  onChange={(e) => setLinkingNpu(formatNPU(e.target.value))}
                  placeholder="0000000-00.0000.0.00.0000"
                  maxLength={25}
                  className={`w-full px-3 py-2 rounded-lg border-2 font-mono text-sm ${
                    linkingNpu && !isValidNPU(linkingNpu) ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
                  }`}
                />
                {linkingNpu && !isValidNPU(linkingNpu) && (
                  <p className="text-[10px] text-red-600 mt-0.5 px-1">Formato inválido — CNJ: NNNNNNN-DD.AAAA.J.TT.OOOO</p>
                )}
              </div>
            </div>
            <div className="p-4 bg-gray-50 border-t border-gray-100 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => { setLinkingWorkspaceId(''); setLinkingNpu(''); }}
                className="px-4 py-2 rounded-lg border border-gray-300 text-xs font-bold text-gray-700 hover:bg-gray-100"
              >Cancelar</button>
              <button
                type="button"
                disabled={!linkingNpu || (linkingNpu && !isValidNPU(linkingNpu))}
                onClick={handleLinkWorkspace}
                className="px-4 py-2 rounded-lg bg-[#20243B] text-[#C5A059] text-xs font-black uppercase tracking-widest hover:bg-[#2c3456] disabled:opacity-40"
              >Vincular</button>
            </div>
          </div>
        </div>
      ) : null}
      {legacyOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => { if (!legacyBusy) { setLegacyOpen(false); setLegacyResult(null); setLegacyInput(''); } }}>
          <div className="bg-white rounded-2xl shadow-2xl border-2 border-red-600 p-6 w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}>

            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">☢️</span>
              <div>
                <h2 className="text-base font-black text-red-700 uppercase tracking-wider">Reset Total — Autos</h2>
                <p className="text-[10px] text-gray-500 mt-0.5">Apaga TUDO: Firestore + pastas físicas do Google Drive</p>
              </div>
            </div>

            {/* ── MODO AUTO ── */}
            <div className="mb-4 p-3 rounded-xl border border-red-200 bg-red-50">
              <p className="text-[10px] font-black uppercase tracking-widest text-red-600 mb-2">🔍 Modo Automático — Varredura Total</p>
              {workspaces.length > 0 ? (
                <>
                  <p className="text-xs text-gray-700 mb-1">
                    Encontrado(s) <strong className="text-red-700">{workspaces.length}</strong> caderno(s) de autos vinculados a{' '}
                    <strong className="text-red-700">
                      {[...new Set(workspaces.flatMap((w) => [
                        String(w?.processPieceFolderId || ''),
                        String(w?.attachmentsFolderId  || ''),
                        String(w?.driveFolderId        || ''),
                      ]).filter(Boolean))].length}
                    </strong>{' '}pasta(s) no Google Drive.
                  </p>
                  <p className="text-[10px] text-red-600 font-bold mb-3">⚠️ Irreversível — banco + Drive serão limpos completamente.</p>
                  <button
                    type="button"
                    onClick={handleAutoWipe}
                    disabled={legacyBusy}
                    className="w-full py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-extrabold uppercase tracking-widest disabled:opacity-50 transition"
                  >{legacyBusy ? '⚙️ Apagando tudo...' : '☢️ Apagar Tudo Agora — Banco + Drive'}</button>
                </>
              ) : (
                <p className="text-xs text-green-700 font-bold">✅ Banco de dados limpo. Nenhum caderno encontrado no Firestore.</p>
              )}
            </div>

            {/* ── MODO MANUAL ── */}
            <div className="p-3 rounded-xl border border-gray-200 bg-gray-50">
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-2">🔧 Modo Manual — IDs Avulsos do Drive</p>
              <p className="text-xs text-gray-500 mb-2">Para pastas órfãs que sobraram no Drive (Firestore já limpo). Cole os IDs — um por linha.</p>
              <textarea
                value={legacyInput}
                onChange={(e) => setLegacyInput(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-red-400 resize-none placeholder-gray-300 bg-white"
                placeholder={"1BcDeFgH_abc123\n2KlMnOpQ_xyz456"}
                disabled={legacyBusy}
              />
              <button
                type="button"
                onClick={handleLegacyDriveClean}
                disabled={!legacyInput.trim() || legacyBusy}
                className="w-full mt-2 py-2 rounded-lg bg-gray-800 hover:bg-gray-900 text-white text-xs font-extrabold uppercase tracking-widest disabled:opacity-50 transition"
              >{legacyBusy ? '⚙️ Limpando...' : '🗑️ Deletar IDs Manuais do Drive'}</button>
            </div>

            {/* ── Resultado ── */}
            {legacyResult && (
              <div className={`mt-3 p-3 rounded-lg text-xs font-bold ${
                legacyResult.fail > 0
                  ? 'bg-amber-50 border border-amber-200 text-amber-800'
                  : 'bg-green-50 border border-green-200 text-green-800'
              }`}>
                {legacyResult.fsDeleted != null && (
                  <span className="block mb-1">🗑️ {legacyResult.fsDeleted} caderno(s) apagado(s) do Firestore.</span>
                )}
                ✅ {legacyResult.ok}/{legacyResult.total} pasta(s) do Drive deletada(s) com sucesso.
                {legacyResult.fail > 0 && (
                  <span className="block mt-1 text-amber-700">⚠️ {legacyResult.fail} falha(s) — pasta já deletada, ID inválido ou servidor offline.</span>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={() => { setLegacyOpen(false); setLegacyResult(null); setLegacyInput(''); }}
              disabled={legacyBusy}
              className="w-full mt-3 py-2 rounded-lg border border-gray-300 text-xs font-bold text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition"
            >Fechar</button>
          </div>
        </div>
      )}
    </>
  );
}
